import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Camera } from 'lucide-react';

interface Props {
  /** Small line above the headline — normally the name of the section that is closed. */
  label: string;
  /** Headline. A "\n" starts a new line. */
  title: string;
  subtitle: string;
  icon?: React.ElementType;
}

/**
 * The screen shown in place of a section that is switched off in admin.
 * Everything sits in one centred column so it reads the same on a phone and a wide desktop.
 */
const ComingSoonPage: React.FC<Props> = ({ label, title, subtitle, icon: Icon = Camera }) => {
  const [time, setTime] = useState({ hh: '00', mm: '00', ss: '00' });

  useEffect(() => {
    const tick = (): void => {
      const d = new Date();
      setTime({
        hh: String(d.getHours()).padStart(2, '0'),
        mm: String(d.getMinutes()).padStart(2, '0'),
        ss: String(d.getSeconds()).padStart(2, '0'),
      });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="min-h-screen bg-white dark:bg-black flex items-center justify-center px-4 sm:px-8 pt-24 sm:pt-32 pb-16 transition-colors duration-700 relative overflow-hidden">
      {/* Decorative grain pattern */}
      <div className="absolute inset-0 opacity-[0.015] dark:opacity-[0.03] pointer-events-none" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 200 200\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'n\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23n)\' opacity=\'0.5\'/%3E%3C/svg%3E")' }} />

      {/* Side rules */}
      <div className="absolute left-8 top-1/2 -translate-y-1/2 hidden lg:flex flex-col items-center gap-3">
        <div className="w-px h-32 bg-gradient-to-b from-transparent via-black/20 dark:via-white/20 to-transparent" />
        <span className="text-[8px] font-bold uppercase tracking-[0.5em] text-black/30 dark:text-white/30 [writing-mode:vertical-rl] rotate-180">Tbilisi · Georgia</span>
        <div className="w-px h-32 bg-gradient-to-b from-transparent via-black/20 dark:via-white/20 to-transparent" />
      </div>
      <div className="absolute right-8 top-1/2 -translate-y-1/2 hidden lg:flex flex-col items-center gap-3 font-mono text-[10px] text-black/30 dark:text-white/30">
        <span>{time.hh}</span>
        <span>{time.mm}</span>
        <span>{time.ss}</span>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        className="max-w-3xl mx-auto text-center relative z-10"
      >
        {/* Top label */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.6 }}
          className="flex items-center justify-center gap-3 mb-8 sm:mb-12"
        >
          <div className="w-10 h-px bg-black/20 dark:bg-white/20" />
          <span className="text-[9px] font-bold uppercase tracking-[0.4em] text-black/40 dark:text-white/40">
            {label}
          </span>
          <div className="w-10 h-px bg-black/20 dark:bg-white/20" />
        </motion.div>

        {/* Icon */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="mb-8 sm:mb-12 inline-flex items-center justify-center w-20 h-20 sm:w-24 sm:h-24 rounded-full border border-black/10 dark:border-white/10 bg-white dark:bg-black shadow-sm"
        >
          <Icon size={28} className="text-black/60 dark:text-white/60" strokeWidth={1.5} />
        </motion.div>

        {/* Main headline */}
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.8 }}
          className="text-5xl sm:text-7xl md:text-8xl lg:text-[9rem] font-syncopate font-bold uppercase tracking-tighter text-black dark:text-white leading-[0.85] mb-6 sm:mb-8 break-words"
        >
          {title.split('\n').map((line, i) => (
            <span key={i} className="block">{line}</span>
          ))}
        </motion.h1>

        {/* Tagline */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.8 }}
          className="text-sm sm:text-base text-black/50 dark:text-white/50 max-w-md mx-auto leading-relaxed mb-10 sm:mb-14 font-light"
        >
          {subtitle}
        </motion.p>

        {/* Bottom signature */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7, duration: 1 }}
          className="mt-16 sm:mt-24 flex items-center justify-center gap-4"
        >
          <div className="w-12 h-px bg-black/15 dark:bg-white/15" />
          <span className="text-[8px] font-bold uppercase tracking-[0.5em] text-black/25 dark:text-white/25">
            Gallery Fotoatelier
          </span>
          <div className="w-12 h-px bg-black/15 dark:bg-white/15" />
        </motion.div>
      </motion.div>
    </div>
  );
};

export default ComingSoonPage;
